import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import cx from 'classnames';

import { useAuth0 } from '@auth0/auth0-react';
import { stringToJSON } from '~helpers/common';

import { useOrder } from '~components/listing/useOrder';
import FiltersButton from '~components/listing/filters/button';
import Sort from '~components/listing/sort';
import ResultsCount from '~components/listing/results-count';
import { useListingState } from '~components/listing/useListingState';
import NewMatchesBanner from '~components/listing/NewMatchesBanner';
import { parseNewMatchesParams } from '~components/listing/newMatchesParams';
import MapToggle from '~components/map/map-toggle/map-toggle';

import ServerContext from '~src/serverContext';
import { DEFAULT_SEARCH_PAGE_SIZE } from '~helpers/defaultSearch';
import { useFilters } from './filters/useFilters';
import Filters from './filters';

import ResultsList from './results-list';
import ResultsMap from './results-map';
// import AddOfferButton from './add-offer';

import LocationContext from '../locationContext';
import { ORDER } from './order';

import { searchCriteria } from './searchCriteria';

import '~components/listing/style.scss';
// import { SidebarCities } from '~components/footer/cities';
import { LocationSearch } from '~components/forms/location';

import BottomNav from '~components/bottom-bar/BottomNav';
import Button from '~components/button';
import { RxCross2 } from "react-icons/rx";
import { MdKeyboardArrowRight } from "react-icons/md" ;


const WorkersList = ({ isCityPage = false, onMapViewChange }) => {
    const history = useHistory();
    const location = useLocation();
    const intl = useIntl();
    const cache = useContext(ServerContext);
    
    const domainConfig = useSelector((state) => state.domainConfig);
    
    const timeoutRef = useRef();
    const pageEffectMountedRef = useRef(false);
    const newMatchesEffectMountedRef = useRef(false);

    const { locationDetails, pricingDetails, isLoadingLocation, locationTouched, onChangeLocation } = useContext(LocationContext);

    const serverLocation = isCityPage ? cache.get('serverLocation') : null;
    // A client-side navigation has no server location; derive the slug-keyed cache key
    // from the URL instead.
    const pathnameSlug = isCityPage ? location.pathname.substring(1).toLowerCase().replace(/\/+$/g, '') : '';
    const initialSlug = serverLocation?.slug || pathnameSlug;

    // City pages search by slug, like the server-side query; the URL slug wins over the
    // resolved location's, which can carry diacritics.
    if (locationDetails) {
      locationDetails.isCityPage = isCityPage;
      if (isCityPage) {
        locationDetails.slug = initialSlug;
      }
    }
    
    const { isAuthenticated } = useAuth0();
    const LISTING_ITEMS = !isAuthenticated ? DEFAULT_SEARCH_PAGE_SIZE : 20;

  // Initialize order with URL parameter if present
  const initializeOrder = () => {
    if (typeof window === 'undefined') {
      return ORDER[0]; // Return default during SSR
    }
    
    const params = new URLSearchParams(location.search);
    const sortParam = params.get('sort');
    
    if (sortParam) {
      const matchingOrder = ORDER.find(o => o.value === sortParam);
      if (matchingOrder) {
        return matchingOrder;
      }
    }
    return ORDER[0]; // default order
  };

  const [order, selectedOrder, setOrder, orderTouched] = useOrder(ORDER, initializeOrder());
  const { newMatchesMode, notificationId } = parseNewMatchesParams(location.search);
  const newMatches = { newMatchesMode, notificationId };

  // Shared by the results snapshot and the filter persistence so both halves of the
  // session state restore (and expire) together. Keyed on the URL, not on the resolved
  // location: the latter arrives from the API after mount, too late to seed the first
  // render, and the saved scroll position would then land on a page without its list.
  const listingCacheKeyParts = ['workers', location.pathname, location.search];

  const { filters, updateFilters, filtersTouched, areFiltersInitialized } = useFilters({
    cacheKeyParts: listingCacheKeyParts,
    persistFilters: !!locationDetails?.locationId,
  });

  const [delayedRender, setDelayedRender] = useState(false);
  const [isShowingMap, setIsShowingMap] = useState(false);
  const [isLoadedOnce, setIsLoadedOnce] = useState(false);
  const [isSortVisible, setIsSortVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const defaultCriteria = useRef(
    searchCriteria({
      location: locationDetails,
      currency: pricingDetails?.currency,
      pagination: {
        limit: LISTING_ITEMS,
        offset: 0,
      },
      order: selectedOrder,
      filters,
      newMatches,
    })
  ).current;

  const parseResult = (r) => {
    if (!r) {
      return null;
    }

    return {
      personId: r.id,
      firstName: r.first_name,
      profilePicture: stringToJSON(r.profile_picture),
      lastLogin: r.last_login,
      registerDate: r.registered_date,
      isOnline: r.is_online,
      personalNote: r.personal_note,
      verification: [
        {
          type: 'email',
          isVerified: (r.verification.find((item) => item.type === 'EMAIL') || {}).is_verified || false,
        },
        {
          type: 'sms',
          isVerified: (r.verification.find((item) => item.type === 'SMS') || {}).is_verified || false,
        },
      ],
      score: r.score,
      price: r.price,
      location: {
        locationId: r.location.place_id,
        place_id: r.location.place_id,
        city: r.location.city,
        slug: r.location.slug,
        country: r.location.country,
        latitude: r.location.latitude,
        longitude: r.location.longitude,
      },
      experience: r.experience,
      days: r.days,
      published: r.published,
      slug: r.slug,
    };
  };

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page') || '1', 10);
  const currentOffset = (currentPage - 1) * LISTING_ITEMS;


  const { results, count, newMatchesTotal, offset, isLoadingResults, isLoadingCount, isLoadingMore, updateListing, reorder, showMore, consumeFreshSnapshot } =
    useListingState({
      cacheKeyParts: listingCacheKeyParts,
      criteria: {
        ...defaultCriteria,
        pagination: {
          limit: LISTING_ITEMS,
          offset: currentOffset,
        },
      },
      matchRequestName: 'workers',
      countRequestName: 'workers',
      matchMockFileName: 'workers/get-workers-by-criteria',
      countMockFileName: 'workers/count-workers-by-criteria',
      itemsPerPage: LISTING_ITEMS,
      parseResult,
    });

  const getCurrentCriteria = () => {
    const pagination = !isShowingMap
      ? {
          limit: LISTING_ITEMS,
          offset: currentOffset,
        }
      : null;

    const nFilters = { ...filters };
    if (filters.price === null && pricingDetails?.maxPrice) {
      nFilters.price = pricingDetails.maxPrice;
    }

    return searchCriteria({
      ...defaultCriteria,
      location: locationDetails,
      currency: pricingDetails?.currency,
      pagination,
      order: selectedOrder,
      filters: nFilters,
      newMatches,
    });
  };

  // Helper to update URL with new page
  const setPage = (page) => {
    const params = new URLSearchParams(location.search);
    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page);
    }

    // Skip the push when the URL would not change (e.g. a filter change while already
    // on page 1) - a same-URL push still creates a history entry with a fresh location
    // key, and the scroll restoration falls back to jumping to the top for unknown keys.
    // The skipped navigation still owes its scroll reset, but only where the list grows
    // by scrolling and can shrink far below the current position.
    if (params.toString() === new URLSearchParams(location.search).toString()) {
      if (isMobile) {
        window.scrollTo(0, 0);
      }
      return;
    }

    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  // Effect to handle page changes from URL
  useEffect(() => {
    // The first fetch belongs to the load effect below. Without this both would claim the
    // restored snapshot on mount and the loser would refetch page one over it.
    if (!pageEffectMountedRef.current) {
      pageEffectMountedRef.current = true;
      return;
    }

    if (!locationTouched || !locationDetails || !locationDetails.locationId || isLoadingLocation || !areFiltersInitialized) return;

    // Snapshot already showing for this page (e.g. paging back to a visited page) - skip.
    if (consumeFreshSnapshot()) {
      return;
    }

    const pagination = !isShowingMap
      ? {
          limit: LISTING_ITEMS,
          offset: currentOffset,
        }
      : null;
    const newCriteria = searchCriteria({
      ...defaultCriteria,
      location: locationDetails,
    //   isCityPage: isCityPage,
      currency: pricingDetails?.currency,
      filters,
      order: selectedOrder,
      pagination,
      newMatches,
    });

    updateListing(newCriteria);
  }, [currentPage, isShowingMap]);

  const [desktopFiltersVisible, setDesktopFiltersVisible] = useState(true);
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

  // Add mounted ref
  const isMounted = useRef(true);
  
  // Set up cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Modify state updates to check mounted status
  const toggleMap = () => {
    if (!isMounted.current) return;
    
    if (isShowingMap) {
      setIsShowingMap(false);
      setDesktopFiltersVisible(true);
      if (onMapViewChange) {
        onMapViewChange(false);
      }
      return;
    }

    if (!locationDetails || !locationDetails.locationId) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setDelayedRender(true);
    setIsShowingMap(true);
    setDesktopFiltersVisible(false);
    if (onMapViewChange) {
      onMapViewChange(true);
    }

    const newCriteria = searchCriteria({
      location: locationDetails,
      currency: pricingDetails?.currency,
      order: selectedOrder,
      filters,
      newMatches,
    });

    const updatePromise = updateListing(newCriteria, !isAuthenticated);
    timeoutRef.current = setTimeout(() => {
      if (isMounted.current) {
        setDelayedRender(false);
      }
    }, 0);

    return () => {
      if (updatePromise && typeof updatePromise.cancel === 'function') {
        updatePromise.cancel();
      }
    };
  };

  useEffect(() => {
    if (!orderTouched) {
      return;
    }

    // Reset to page 1 on sort change
    setPage(1);

    const newCriteria = searchCriteria({
      ...defaultCriteria,
      location: locationDetails,
      currency: pricingDetails?.currency,
      filters,
      pagination: {
        limit: LISTING_ITEMS,
        offset: 0,
      },
      order: selectedOrder,
      newMatches,
    });

    const reorderPromise = reorder(newCriteria);

    return () => {
      if (reorderPromise && typeof reorderPromise.cancel === 'function') {
        reorderPromise.cancel();
      }
    };
  }, [selectedOrder]);

  useEffect(() => {
    if (!filtersTouched ) return;
    if (isLoadingLocation || !locationDetails?.locationId) return; 

    // Reset to page 1 on filter change
    setPage(1);

    const pagination = !isShowingMap
      ? {
          limit: LISTING_ITEMS,
          offset: 0,
        }
      : null;

    const newCriteria = searchCriteria({
      ...defaultCriteria,
      location: locationDetails,
      currency: pricingDetails?.currency,
      filters,
      order: selectedOrder,
      pagination,
      newMatches,
    });

    const updatePromise = updateListing(newCriteria);
    
    return () => {
      if (updatePromise && typeof updatePromise.cancel === 'function') {
        updatePromise.cancel();
      }
    };
  }, [filters]);

  // Refetch when leaving (or entering) new-matches mode. history.replace in the banner
  // only updates the URL; nothing else was watching these params.
  useEffect(() => {
    if (!newMatchesEffectMountedRef.current) {
      newMatchesEffectMountedRef.current = true;
      return;
    }

    if (!locationTouched || !locationDetails?.locationId || isLoadingLocation || !areFiltersInitialized) {
      return;
    }

    setPage(1);

    const pagination = !isShowingMap
      ? {
          limit: LISTING_ITEMS,
          offset: 0,
        }
      : null;

    const nFilters = { ...filters };
    if (filters.price === null && pricingDetails?.maxPrice) {
      nFilters.price = pricingDetails.maxPrice;
    }

    const newCriteria = searchCriteria({
      ...defaultCriteria,
      location: locationDetails,
      currency: pricingDetails?.currency,
      filters: nFilters,
      order: selectedOrder,
      pagination,
      newMatches,
    });

    updateListing(newCriteria);
  }, [newMatchesMode, notificationId]);

  useEffect(() => {
    if (!locationTouched || !locationDetails || !locationDetails.locationId || isLoadingLocation || !areFiltersInitialized) return;

    // Fresh snapshot already showing - skip the initial fetch (data is within TTL). Only
    // the first load after a restore is skipped; any filter/sort/page change reloads.
    if (consumeFreshSnapshot()) {
      setIsLoadedOnce(true);
      setDelayedRender(false);
      return;
    }

    const pagination = !isShowingMap
      ? {
          limit: LISTING_ITEMS,
          offset: currentOffset,
        }
      : null;

    const nFilters = { ...filters };
    if (filters.price === null) {
      nFilters.price = pricingDetails.maxPrice;
    }

    const newCriteria = searchCriteria({
      location: locationDetails,
      currency: pricingDetails?.currency,
      pagination,
      order: selectedOrder,
      filters: nFilters,
      newMatches,
    });

    // Store the update function call in a variable
    const updatePromise = updateListing(newCriteria, !isAuthenticated && !isLoadedOnce);
    setIsLoadedOnce(true);

    const timeoutId = setTimeout(() => setDelayedRender(false), 0);

    return () => {
      // Clear the timeout
      clearTimeout(timeoutId);
      // Cancel any pending update if component unmounts
      if (updatePromise && typeof updatePromise.cancel === 'function') {
        updatePromise.cancel();
      }
    };

  }, [isLoadingLocation, areFiltersInitialized]);

  useEffect(() => {
    if (isLoadingLocation) {
      setDelayedRender(true);
    }
  }, [isLoadingLocation]);

  useEffect(() => {
    if (locationDetails?.city) {
      cache.del('serverCityName');
    }
  }, [locationDetails?.city]);

  useEffect(() => {
    return () => {
      if (cache && typeof cache.del === 'function' && isMounted.current) {
        cache.del(`cityWorkers${slug}`);
        cache.del(`cityWorkersCount${slug}`);
      }
    };
  }, [slug]);

  // ssr section start
  const [slug, setSlug] = useState(initialSlug);

  // Get server cache data synchronously for SSR
  const serverWorkers = isCityPage ? cache.get(`cityWorkers${initialSlug}`) : null;
  const serverWorkersCount = isCityPage ? cache.get(`cityWorkersCount${initialSlug}`) : null;

  // Process server cache data through parseResult
  const serverMatches = serverWorkers ? serverWorkers.map(parseResult) : [];

  // Server-rendered workers win the first paint so hydration matches the server markup;
  // they are deleted (below) as soon as fresh results arrive.
  const displayMatches = serverMatches.length > 0 ? serverMatches : results;
  const displayCount = newMatchesMode && typeof newMatchesTotal === 'number'
    ? newMatchesTotal
    : (serverMatches.length > 0 ? (serverWorkersCount || 0) : (count || 0));

  // Clean up server cache when real data arrives
  useEffect(() => {
    if (isCityPage && results.length > 0) {
      cache.del(`cityWorkers${slug}`);
      cache.del(`cityWorkersCount${slug}`);
    }
  }, [results, isCityPage, slug]);

  // Update slug when URL changes
  useEffect(() => {
    if (isCityPage) {
      const pathSlug = window.location.pathname.substring(1).toLowerCase().replaceAll(/\/$/g, '');
      setSlug(pathSlug);
    }
  }, [isCityPage]);
  // ssr section end

  return (
    <>    
    <BottomNav 
      onFilterClick={() => setMobileFiltersVisible(!mobileFiltersVisible)}
      filters={filters}
      defaultPreference={0}
      // onSortClick={() => {
      //   if (!isLoadingLocation && !isLoadingResults && !isLoadingCount && !delayedRender) {
      //     setIsSortVisible(!isSortVisible);
      //   }
      // }}
      // onMapClick={toggleMap}
      // isMapActive={isShowingMap} 
      // isMapDisabled={
      //   !locationDetails ||
      //   !locationDetails.locationId ||
      //   isLoadingLocation ||
      //   isLoadingResults ||
      //   isLoadingCount ||
      //   !count
      // }
      isLoading={isLoadingLocation || isLoadingResults || isLoadingCount || delayedRender}
      // locationId={locationDetails?.locationId}
    />

      { !isShowingMap ? (
          <div className="srch-wrap city-page">
            <div className="srch-lst">
              {/* City SEO copy (H1 + explainer) now lives in the reusable
                  CitySeoContent component rendered by containers/city/index.jsx,
                  so the city page has a single canonical H1. */}

                {isCityPage && isMobile &&
                <div className="search-location-wrapper">
                    <LocationSearch
                        isDataLoading={isLoadingLocation || delayedRender}
                        location={!isCityPage ? locationDetails : {}}
                        placeholder={intl.formatMessage({
                            id: 'containers.workers.subheader.address',
                            defaultMessage: 'Address',
                        })}
                        name="address"
                        className="c-autocomplete--focus-disabled" 
                        onSubmit={(loc) => {
                            if (loc && loc.place_id) {
                            history.push({
                                pathname: '/workers',
                                search: `?locationId=${loc.place_id}`,
                                state: {
                                location: {
                                    locationId: loc.place_id,
                                    address: loc.address
                                }
                                }
                            });
                            if (!isCityPage && onChangeLocation) {
                                onChangeLocation(loc.place_id);
                            }
                            }
                        }}
                        />
                    </div>
                }
                {isCityPage && 
                  <div className="city_cltd-link">
                    <Link to="/intake/worker" rel="nofollow">{intl.formatMessage({
                        id: 'containers.workers.get-listed-as-cleaner',
                        defaultMessage: 'Get Listed as cleaner',
                      })}</Link>
                  </div>
                }
              </div>
          </div>
        ) : (
          <Button onClick={toggleMap} className="c-map-close">
            <RxCross2 size={24}/>
          </Button>
      )}

    <div className="c-matches__container">

      <aside
        className={cx('c-page__sidebar c-page__sidebar--matches c-page__sidebar--tablet-hidden', {
          'l-hidden': !desktopFiltersVisible,
        })}
      >
        {/* <div className="c-sidebar__title">
          {intl.formatMessage({
            id: 'containers.workers.sidebar.title',
            defaultMessage: 'Address',
          })}
        </div> */}
        <div className="c-collapsible__header c-sidebar__adres-title">
          {intl.formatMessage(
            {
              id: 'containers.workers.sidebar.address_title',
              defaultMessage: 'Address',
            }
          )}
        </div>
        <div>
        {intl.formatMessage(
          {
            id: 'containers.workers.sidebar.subTitle',
            defaultMessage: 'Fill in your postcode or address to see the cleaners that work at your address',
          }
        )}
        </div>
        <Filters
          values={filters}
          onChange={updateFilters}
          showMobileFilters={mobileFiltersVisible}
          showDesktopFilters={desktopFiltersVisible}
          isLoading={isLoadingLocation || delayedRender}
          isCityPage={isCityPage}
          sortOrder={order}
          sortValue={selectedOrder}
          sortOnChange={setOrder}
          changeMobileFiltersVisible={setMobileFiltersVisible}
        />
        
        {/* {isCityPage && <SidebarCities displayNeighborhoods={true} />} */}
        
      </aside>
      <div className={cx('c-matches__list c-page__main', { 'c-matches__list--map-view': isShowingMap, 'c-matches__list--city-page': isCityPage })}>

        <div className="c-matches__list-header">
          <div className="c-matches__list-header-left">
            <ResultsCount count={count} isLoading={isLoadingLocation || isLoadingCount || delayedRender} />
            <FiltersButton
              className={cx({ 'c-filters-button--map-visible': isShowingMap })}
              isLoading={isLoadingLocation || isLoadingResults || delayedRender}
              onClick={() => setMobileFiltersVisible(!mobileFiltersVisible)}
            />
          </div>
          
          <div className="c-matches__list-header-right">
            
            <MapToggle isLoading={isLoadingLocation || isLoadingResults || isLoadingCount || delayedRender} 
                          isShowingMap={isShowingMap}
                          disabled={
                            !locationDetails ||
                            !locationDetails.locationId ||
                            isLoadingLocation ||
                            isLoadingResults ||
                            isLoadingCount ||
                            !count
                          }
                          onClick={toggleMap}
            />
            {(!isLoadingLocation && !count) || isShowingMap ? null : (
              <Sort
                order={order}
                value={selectedOrder}
                isLoading={isLoadingLocation || isLoadingResults || isLoadingCount || delayedRender}
                onChange={setOrder}
                isVisible={isSortVisible}
                onVisibleChange={setIsSortVisible}
              />
            )}
          </div>
        </div>

        {!isShowingMap ? (
          <>
            {newMatchesMode && (
              <NewMatchesBanner count={displayCount} />
            )}
          <ResultsList
            // matches={results} // without ssr
            // count={count} // without ssr
            key={locationDetails?.locationId || 'results'}
            matches={displayMatches}
            count={displayCount}
            isCityPage={isCityPage}
            cityName={locationDetails?.city || locationDetails?.description || locationDetails?.country || cache.get('serverCityName')}
            citySlug={locationDetails?.slug}
            locationId={locationDetails?.locationId}
            isLoading={isLoadingResults || isLoadingLocation || delayedRender}
            isLoadingMore={isLoadingMore}
            onShowMore={() => showMore(getCurrentCriteria())}
            currentPage={currentPage}
            itemsPerPage={LISTING_ITEMS}
            newMatchesMode={newMatchesMode}
          />
          </>
        ) : (
          <ResultsMap
            matches={results}
            isLoading={isLoadingResults || isLoadingCount || isLoadingLocation || delayedRender}
            locationId={locationDetails?.locationId}
          />
        )}

        {/* {count > 0 &&
        offset + LISTING_ITEMS >= count &&
        !isLoadingMore &&
        !isLoadingResults &&
        !isLoadingCount &&
        !isLoadingLocation &&
        !delayedRender ? (
          <AddOfferButton
            destination={`/intake/client${
              locationDetails?.locationId ? `?locationId=${locationDetails?.locationId}` : ''
            }&showFields=true`}
          />
        ) : null} */}
      </div>
    </div>
    </>
  );
};

export default WorkersList;
